import type { Event, EventTemplate } from 'nostr-tools/core';
import { decode } from 'nostr-tools/nip19';
import type { Artwork, BadgeDefinition } from './api';
import type { ApprovalHandler, BadgeBatchContext, SignerConnection } from './signers';
import { requireExactSignerResult } from './signerSafety';

export type RecipientRow = {
  source: string;
  label: string;
  pubkey: string | null;
  subjectProfileURL: string;
  error: string | null;
  duplicate: boolean;
};

export async function signBadgeDefinition(
  connection: SignerConnection,
  input: { identifier: string; name: string; description: string; artwork: Artwork },
  onApproval?: ApprovalHandler
): Promise<Event> {
  const tags: string[][] = [
    ['d', input.identifier],
    ['name', input.name.trim()],
    ['description', input.description.trim()],
    ['image', input.artwork.image.url, `${input.artwork.image.width}x${input.artwork.image.height}`],
    ...input.artwork.thumbnails.map((thumb) => ['thumb', thumb.url, `${thumb.width}x${thumb.height}`])
  ];
  const unsigned = template(30009, tags);
  const signed = onApproval && connection.signEventWithApproval
    ? await connection.signEventWithApproval(unsigned, onApproval)
    : await connection.signer.signEvent(unsigned);
  return requireExactSignerResult(unsigned, signed, connection.pubkey);
}

export async function signBadgeAwards(
  connection: SignerConnection,
  badge: BadgeDefinition,
  recipients: string[],
  relayHint: string,
	context?: BadgeBatchContext
): Promise<Event[]> {
	const address = `30009:${connection.pubkey}:${badge.identifier}`;
	const templates = recipients.map((pubkey) => template(8, [['a', address], ['p', pubkey, relayHint]]));
	if (connection.signEvents) {
		const signed = await connection.signEvents(templates, context);
		if (signed.length !== templates.length) throw new Error('The signer returned the wrong number of badge awards.');
		return signed.map((event, index) => requireExactSignerResult(templates[index], event, connection.pubkey));
	}
	const events: Event[] = [];
	for (const event of templates) events.push(requireExactSignerResult(event, await connection.signer.signEvent(event), connection.pubkey));
	return events;
}

export async function signBadgeRevocation(
  connection: SignerConnection,
  awardEventID: string,
  reason = 'Revoked by issuer.'
): Promise<Event> {
  const unsigned = {
    kind: 5,
    tags: [['e', awardEventID], ['k', '8']],
    content: reason.trim().slice(0, 500),
    created_at: Math.floor(Date.now() / 1000)
  };
  return requireExactSignerResult(unsigned, await connection.signer.signEvent(unsigned), connection.pubkey);
}

export function parseRecipients(value: string): RecipientRow[] {
  const seen = new Set<string>();
  return value.split(/\r?\n/).map((source) => {
    const trimmed = source.trim();
    if (!trimmed) return null;
    const columns = parseRow(trimmed);
    if (isHeader(columns)) return null;
    const candidate = columns.key;
    let pubkey: string | null = null;
    let error: string | null = null;
    try {
      pubkey = normalizePubkey(candidate);
    } catch (cause) {
      error = cause instanceof Error ? cause.message : 'Invalid public key';
    }
	if (!error && columns.subjectProfileURL) {
		try {
			const profile = new URL(columns.subjectProfileURL);
			if (profile.protocol !== 'https:' || profile.username || profile.password || profile.hash) throw new Error();
		} catch {
			error = 'Profile URL must be a public HTTPS URL without a fragment';
		}
	}
    const duplicate = pubkey !== null && seen.has(pubkey);
    if (pubkey) seen.add(pubkey);
	return { source, label: columns.label, pubkey, subjectProfileURL: columns.subjectProfileURL, error, duplicate };
  }).filter((row): row is RecipientRow => row !== null);
}

export function normalizePubkey(value: string): string {
  const candidate = value.trim().replace(/^nostr:/i, '');
  if (/^[0-9a-f]{64}$/.test(candidate)) return candidate;
  if (candidate.toLowerCase().startsWith('npub1')) {
    const decoded = decode(candidate.toLowerCase());
    if (decoded.type !== 'npub') throw new Error('Expected an npub');
    return decoded.data;
  }
  throw new Error('Use an npub or 64-character hex public key');
}

export function identifierFromName(value: string): string {
  return value.toLowerCase().trim().replace(/[^a-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
}

function parseRow(value: string): { key: string; label: string; subjectProfileURL: string } {
  const fields = parseCSVLine(value);
	if (fields.length < 2) return { key: fields[0] ?? '', label: '', subjectProfileURL: '' };
  const first = fields[0].trim();
  const second = fields[1].trim();
	const third = fields[2]?.trim() ?? '';
	if (/^(npub1|nostr:npub1|[0-9a-f]{64})/i.test(first)) {
		if (!third && /^https:\/\//i.test(second)) return { key: first, label: '', subjectProfileURL: second };
		return { key: first, label: second, subjectProfileURL: third };
	}
	return { key: second, label: first, subjectProfileURL: third };
}

function parseCSVLine(value: string): string[] {
  const fields: string[] = [];
  let field = '';
  let quoted = false;
  for (let index = 0; index < value.length; index++) {
    const character = value[index];
    if (character === '"') {
      if (quoted && value[index + 1] === '"') {
        field += '"';
        index++;
      } else {
        quoted = !quoted;
      }
    } else if (character === ',' && !quoted) {
      fields.push(field);
      field = '';
    } else {
      field += character;
    }
  }
  fields.push(field);
  return fields;
}

function isHeader(row: { key: string; label: string }): boolean {
  const key = row.key.toLowerCase().replace(/[_ -]/g, '');
  const label = row.label.toLowerCase().replace(/[_ -]/g, '');
  return ['npub', 'pubkey', 'nostr', 'nostrpubkey'].includes(key) && ['name', 'label', 'displayname'].includes(label);
}

function template(kind: number, tags: string[][]): EventTemplate {
  return { kind, tags, content: '', created_at: Math.floor(Date.now() / 1000) };
}
